import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    CircularProgress,
    Divider,
    List,
    ListItem,
    ListItemText,
    Tabs,
    Tab,
    Button,
    Chip
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import VideocamIcon from '@mui/icons-material/Videocam';
import LanguageIcon from '@mui/icons-material/Language';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditTrackingService from '../../services/EditTrackingService';

export default function UserEditStats({ folderPath, videoId, filterMode }) {
    const [stats, setStats] = useState(null);
    const [videoStats, setVideoStats] = useState([]);
    const [languageStats, setLanguageStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [videoLanguageLoading, setVideoLanguageLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        const fetchUserStats = async () => {
            setLoading(true);
            try {
                // Get basic stats
                const data = await EditTrackingService.getUserStats(folderPath, videoId);
                console.log('Fetched user stats:', data);
                // add time by level to get total time and add that to 
                setStats(data);
                // add time by level to get total time and add that to stats
                if (data.totalTimeByLevel) {
                    const totalTime = Object.values(data.totalTimeByLevel).reduce((acc, time) => acc + time, 0);
                    data.totalTime = totalTime;
                } else {
                    data.totalTime = 0; // Default to 0 if no time data
                }

                
                // Also load detailed stats
                fetchDetailedStats();
            } catch (error) {
                console.error('Error fetching user stats:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchUserStats();
    }, [folderPath, videoId, filterMode]);
    
    const fetchDetailedStats = async () => {
        setVideoLanguageLoading(true);
        try {
            // Get video breakdown
            const videos = await EditTrackingService.getUserVideoBreakdown();
            setVideoStats(videos);
            
            // Get language breakdown
            const languages = await EditTrackingService.getUserLanguageBreakdown();
            setLanguageStats(languages);
        } catch (error) {
            console.error('Error fetching detailed stats:', error);
        } finally {
            setVideoLanguageLoading(false);
        }
    };

    const formatTime = (ms) => {
        if (!ms) return '00:00:00';
        return EditTrackingService.formatTime(ms);
    };
    
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    if (!stats) {
        return (
            <Typography variant="body1" color="text.secondary" p={2}>
                No editing statistics available.
            </Typography>
        );
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h5" gutterBottom>
                {folderPath && folderPath !== '/' && (
                    <Typography variant="subtitle2" color="text.secondary">
                        Filtered by folder: {folderPath}
                    </Typography>
                )}
                {videoId && (
                    <Typography variant="subtitle2" color="text.secondary">
                        Filtered by video ID: {videoId}
                    </Typography>
                )}
            </Typography>
            
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
                <Tab 
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AccessTimeIcon sx={{ mr: 1 }} />
                            Summary
                        </Box>
                    } 
                />
                <Tab 
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <VideocamIcon sx={{ mr: 1 }} />
                            Videos
                        </Box>
                    } 
                />
                <Tab 
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LanguageIcon sx={{ mr: 1 }} />
                            Languages
                        </Box>
                    } 
                />
            </Tabs>
            
            {/* Summary Tab */}
            {activeTab === 0 && (
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Time Spent by Level
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                
                                {Object.entries(stats.totalTimeByLevel || {}).map(([level, time]) => (
                                    <Box key={level} sx={{ mb: 1 }}>
                                        <Typography variant="body1">
                                            {level}: <strong>{formatTime(time)}</strong>
                                        </Typography>
                                    </Box>
                                ))}
                                
                                {Object.keys(stats.totalTimeByLevel || {}).length === 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        No time data available
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Edits by Level
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                
                                {Object.entries(stats.totalEditsByLevel || {}).map(([level, edits]) => (
                                    <Box key={level} sx={{ mb: 1 }}>
                                        <Typography variant="body1">
                                            {level}: <strong>Coming Soon ...</strong>
                                        </Typography>
                                    </Box>
                                ))}
                                
                                {Object.keys(stats.totalEditsByLevel || {}).length === 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        No edit data available
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Summary
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ textAlign: 'center', p: 1 }}>
                                            <Typography variant="h5">{stats.totalSessions || 0}</Typography>
                                            <Typography variant="body2" color="text.secondary">Total Sessions</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ textAlign: 'center', p: 1 }}>
                                            <Typography variant="h5">{formatTime(stats.totalTime || 0)}</Typography>
                                            <Typography variant="body2" color="text.secondary">Total Time</Typography>
                                        </Box>
                                    </Grid>
                                    
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}
            
            {/* Videos Tab */}
            {activeTab === 1 && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Videos You've Worked On
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        {videoLanguageLoading ? (
                            <Box display="flex" justifyContent="center" p={3}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : videoStats && videoStats.length > 0 ? (
                            <List>
                                {videoStats.map((video) => (
                                    <ListItem key={video.videoId} divider>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle1">{video.videoName || video.videoId}</Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                    {video.languages.map(lang => (
                                                        <Chip 
                                                            key={lang} 
                                                            label={lang} 
                                                            size="small" 
                                                            variant="outlined"
                                                            sx={{ mr: 0.5 }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4} sm={2}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                                    <Typography>{formatTime(video.time)}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4} sm={2}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <EditIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                                    <Typography>Coming Soon ...</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4} sm={2}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                                    <Typography>{video.sessionsCount || 0} sessions</Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No video data available
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            )}
            
            {/* Languages Tab */}
            {activeTab === 2 && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Languages You've Worked On
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        {videoLanguageLoading ? (
                            <Box display="flex" justifyContent="center" p={3}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : languageStats && languageStats.length > 0 ? (
                            <List>
                                {languageStats.map((lang) => (
                                    <ListItem key={lang.language} divider>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle1">{lang.language}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Used in {lang.videoCount} videos
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6} sm={3}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                                    <Typography>{formatTime(lang.time)}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6} sm={3}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <EditIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                                    <Typography>Coming Soon ...</Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No language data available
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            )}
        </Box>
    );
}